import { useState, useEffect, useRef } from 'preact/hooks';
import { getVisibleTextLength, sliceHtml } from '../utils/html.js';

/**
 * A custom hook to smooth out the rendering of an incoming streaming HTML response
 * by using a 60fps ticker animation loop with catch-up logic.
 *
 * @param {string} fullHtml - The latest full HTML content parsed from the stream.
 * @param {boolean} isStreaming - Flag indicating whether the stream is currently active.
 * @param {number} [typingSpeed] - Baseline typing speed in characters per second.
 * @param {boolean} [organicTyping] - Enables human-like pauses and speed jitter.
 * @param {number} [catchUpSeconds] - After the stream ends, keep typing the remaining text
 *   for at most this many seconds (speeding up as needed) instead of revealing it at once.
 *   0 (default) reveals the rest of the text immediately when the stream ends.
 * @returns {string} The HTML slice that is currently animated/revealed.
 */
export function useStreamSmoother(fullHtml, isStreaming, typingSpeed, organicTyping = false, catchUpSeconds = 0) {
  const [displayedContent, setDisplayedContent] = useState('');

  // True once this view has observed an active stream. Content restored from history or a
  // shared snapshot never streamed here, so it is always shown at once.
  const hasStreamedRef = useRef(false);

  // Storage for the incoming full HTML. We use a ref so updating it 
  // doesn't force the animation loop to restart!
  const rawHtmlRef = useRef(fullHtml);
  rawHtmlRef.current = fullHtml;

  const currentIndex = useRef(0);
  const pauseRemainingMs = useRef(0);

  // Cognitive burst tracking to mimic phrase-based typing (not word-by-word stutters)
  const wordsSinceLastPause = useRef(0);
  const targetBurstLength = useRef(4); // planning break every 3-5 words

  // Extracts pure visible plain-text characters (handling entities as 1 space for exact indexes)
  const getVisibleText = (html) => {
    let text = '';
    let inTag = false;
    let i = 0;
    while (i < html.length) {
      if (html[i] === '<') {
        inTag = true;
      } else if (html[i] === '>') {
        inTag = false;
      } else if (!inTag) {
        if (html[i] === '&') {
          while (i < html.length && html[i] !== ';') {
            i++;
          }
          text += ' '; // stand-in for entity char
        } else {
          text += html[i];
        }
      }
      i++;
    }
    return text;
  };

  useEffect(() => {
    const catchUpMs = (typeof catchUpSeconds === 'number' && catchUpSeconds > 0) ? catchUpSeconds * 1000 : 0;

    const snapToEnd = () => {
      setDisplayedContent(rawHtmlRef.current);
      currentIndex.current = getVisibleTextLength(rawHtmlRef.current);
    };

    if (isStreaming) {
      hasStreamedRef.current = true;
    } else {
      const remainingAtEnd = getVisibleTextLength(rawHtmlRef.current) - currentIndex.current;
      // Snap to the final HTML when catch-up is disabled, nothing is left to type,
      // or the content was never streamed in this view (history, shared snapshot).
      if (!catchUpMs || remainingAtEnd <= 0 || !hasStreamedRef.current) {
        snapToEnd();
        return;
      }
    }

    // While streaming there is no deadline. After the stream ends, the rest of the text
    // must be typed before the deadline (rAF timestamps share the performance.now() clock).
    const deadline = isStreaming ? null : performance.now() + catchUpMs;
    const isCatchingUp = deadline !== null;
    if (isCatchingUp) {
      pauseRemainingMs.current = 0;
    }

    let frameId;
    let lastTime = null;

    // Baseline typing speed: visible characters per millisecond
    // Fallback to 28 characters per second if not specified
    const normalSpeed = 28;
    const configSpeed = (typeof typingSpeed === 'number' && typingSpeed > 0) ? typingSpeed : normalSpeed;
    const baseSpeed = configSpeed / 1000;

    // Pause scale factor: if typing speed is increased, pauses scale up proportionally 
    // so that the cognitive human cadence is always clearly visible and proportional!
    const pauseScale = Math.min(3.0, Math.max(0.6, configSpeed / normalSpeed));

    // The continuous ticker loop
    const tick = (timestamp) => {
      if (lastTime === null) {
        lastTime = timestamp;
      }
      const deltaMs = timestamp - lastTime;
      lastTime = timestamp;

      // Catch-up window is over: reveal whatever is left and stop.
      if (isCatchingUp && timestamp >= deadline) {
        snapToEnd();
        return;
      }

      const target = rawHtmlRef.current;
      const totalVisibleLength = getVisibleTextLength(target);
      const remaining = totalVisibleLength - currentIndex.current;

      if (remaining > 0) {
        // If there's a punctuation/word pause active, count it down and skip typing during this frame
        if (pauseRemainingMs.current > 0) {
          pauseRemainingMs.current -= deltaMs;
          if (pauseRemainingMs.current < 0) {
            pauseRemainingMs.current = 0;
          }
          frameId = requestAnimationFrame(tick);
          return;
        }

        const prevIndex = currentIndex.current;
        const currentIntIndex = Math.floor(prevIndex);

        // Highly refined organic typing speed configuration:
        // - Huge chunks backlogged: speed up heavily to catch up quickly
        // - Steady stream: apply sinus speed jitter to emulate finger travel differences
        let speed = baseSpeed;
        if (remaining > 150) {
          speed = baseSpeed * 4.3;
        } else if (remaining > 50) {
          speed = baseSpeed * 2.3;
        } else if (organicTyping && !isCatchingUp) {
          // Rolling speed waves: smooth sinusoidal pace fluctuation over 50-character blocks (about 10 words)
          // Avoids erratic stuttering on individual letters
          const jitter = 0.85 + 0.3 * (Math.sin(currentIntIndex * 0.12) * 0.5 + 0.5);
          speed = baseSpeed * jitter;
        }

        // After the stream ended, never type slower than needed to finish before the deadline.
        if (isCatchingUp) {
          speed = Math.max(speed, remaining / Math.max(deadline - timestamp, 1));
        }

        // Multiply speed (chars/ms) by elapsed time delta (ms) for perfect frame-rate independence!
        currentIndex.current += speed * deltaMs;

        // Prevent overflowing the string length
        if (currentIndex.current > totalVisibleLength) {
          currentIndex.current = totalVisibleLength;
        }

        // Apply human pauses at punctuation boundaries to mimic cognitive thought breaks.
        // Pauses scale proportionally with configured typingSpeed and are skipped during catch-up.
        const newIntIndex = Math.floor(currentIndex.current);
        if (organicTyping && !isCatchingUp && newIntIndex > currentIntIndex) {
          const visibleText = getVisibleText(target);
          const justTypedChar = visibleText[currentIntIndex];
          if (justTypedChar) {
            if (justTypedChar === '.' || justTypedChar === '!' || justTypedChar === '?') {
              pauseRemainingMs.current = 320 * pauseScale; // up to 320ms * pauseScale pause after sentence ends
              wordsSinceLastPause.current = 0;
              targetBurstLength.current = Math.floor(Math.random() * 3) + 3; // next burst size: 3 to 5 words
            } else if (justTypedChar === ',' || justTypedChar === ';' || justTypedChar === ':' || justTypedChar === '-') {
              pauseRemainingMs.current = 160 * pauseScale; // up to 160ms * pauseScale pause after clause ends
              wordsSinceLastPause.current = 0;
              targetBurstLength.current = Math.floor(Math.random() * 3) + 3;
            } else if (justTypedChar === ' ') {
              wordsSinceLastPause.current++;
              // Pausing after every single word creates robotic stutters.
              // Humans type in "cognitive bursts" (groups of 3-5 words), then make a micro planning pause.
              if (wordsSinceLastPause.current >= targetBurstLength.current) {
                pauseRemainingMs.current = 70 * pauseScale; // planning pause between bursts
                wordsSinceLastPause.current = 0;
                targetBurstLength.current = Math.floor(Math.random() * 3) + 3;
              }
            }
          }
        }

        setDisplayedContent(sliceHtml(target, Math.floor(currentIndex.current)));
      } else if (isCatchingUp) {
        // Everything is typed after the stream ended: show the exact final HTML and stop.
        snapToEnd();
        return;
      }

      // Loop over to the next frame while streaming (or catching up) is active
      frameId = requestAnimationFrame(tick);
    };

    // Start the engine
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
    };

    // CRITICAL FIX: We DO NOT depend on the HTML string here. 
    // This guarantees the loop never restarts or jumps when a new chunk arrives!
  }, [isStreaming, typingSpeed, organicTyping, catchUpSeconds]);

  return displayedContent;
}
